import type { BaseGatewayErrorDTO } from '@cplug/frontend-core/gateways'
import { HttpMethod } from '@cplug/frontend-core/models'
import { HttpStatusCode } from 'axios'
import type { AxiosResponse } from 'axios'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { BaseModalType } from '@cplug/design-system'
import { useFormModalStore } from '@cplug/frontend-core'
import { PaymentRequiredErrorMessage } from '../models/PaymentRequredError'
import MaintenanceView from '../components/organisms/MaintenanceView.vue'
import { scheduleForbiddenRedirect } from '~/shared/utils/forbidden-access/forbiddenAccessRedirect'
import { FORBIDDEN_ACCESS_PAGE_PATH, isIgnoredForbiddenPermission } from '~/shared/models/ForbiddenAccess'
import useForbiddenAccessStore from '~/shared/stores/useForbiddenAccessStore'
import { finalizeLogoutSessionState } from '~/domain/shared/authentication/composables/useSessionRestoreState'
import { navigateTo, useNuxtApp, useRuntimeConfig } from '#imports'

interface ForbiddenErrorData {
  meta?: {
    permission_name?: string
    module_name?: string
    meta?: {
      module_sub_type?: string
    }
  }
}

const CRITICAL_ENDPOINTS = ['companies', 'users', 'auth']
const NOT_REDIRECT_TO_NOT_ALLOWED_ROUTES = ['bulks', 'feature-flags', 'research-customers']
function shouldSkipPlanBlockedRedirect (): boolean {
  const bypassPlanModuleCheck = (useRuntimeConfig().public as { bypassPlanModuleCheck?: boolean | string }).bypassPlanModuleCheck
  return bypassPlanModuleCheck === true || bypassPlanModuleCheck === 'true'
}

const MAINTENANCE_REDIRECT_STATUSES: ReadonlySet<number> = new Set([
  HttpStatusCode.InternalServerError,
  HttpStatusCode.BadGateway,
  HttpStatusCode.ServiceUnavailable,
  HttpStatusCode.GatewayTimeout
])

function parsePermissionSlug (meta: ForbiddenErrorData['meta']): string | null {
  if (typeof meta?.permission_name !== 'string' || meta.permission_name.length === 0) {
    return null
  }

  return meta.permission_name
}

export function useGatewayErrorHandler (url: string, response: AxiosResponse<BaseGatewayErrorDTO>): void {
  const handleForbiddenError = (response: AxiosResponse<BaseGatewayErrorDTO>, url: string): void => {
    const forbiddenData = response.data as ForbiddenErrorData | null

    if (forbiddenData?.meta?.permission_name) {
      const isCriticalEndpoint = CRITICAL_ENDPOINTS.some(endpoint => url.includes(endpoint))

      if (isCriticalEndpoint) {
        const authStore = useAuthenticationStore()
        authStore.logout()
          .then(() => {
            finalizeLogoutSessionState()
            navigateTo('/login', { replace: true })
          })
          .catch(() => {})
        return
      }
    }

    if (NOT_REDIRECT_TO_NOT_ALLOWED_ROUTES.some(endpoint => url.includes(endpoint))) {
      return
    }

    if (forbiddenData?.meta?.module_name) {
      return
    }

    const permissionSlug = parsePermissionSlug(forbiddenData?.meta)

    if (permissionSlug == null) {
      return
    }

    if (isIgnoredForbiddenPermission(permissionSlug)) {
      return
    }

    if (useNuxtApp().$router.currentRoute.value.path === FORBIDDEN_ACCESS_PAGE_PATH) {
      return
    }

    const blockedFrom = useNuxtApp().$router.currentRoute.value.fullPath
    const blockedApiMethod = (response.config?.method as HttpMethod | undefined) ?? HttpMethod.GET
    const forbiddenAccessStore = useForbiddenAccessStore()

    forbiddenAccessStore.addMissingPermissions([permissionSlug], {
      blockedFrom,
      blockedApiUrl: url,
      blockedApiMethod
    })

    scheduleForbiddenRedirect(() => {
      forbiddenAccessStore.markRedirectArrival()
      navigateTo(FORBIDDEN_ACCESS_PAGE_PATH, { replace: true })
    })
  }

  const handlePaymentRequiredError = (): void => {
    if (shouldSkipPlanBlockedRedirect()) {
      return
    }

    if (response.data.message === PaymentRequiredErrorMessage.OverduePayment) {
      navigateTo('/dashboard/overdue-payment', { replace: true })
    } else {
      navigateTo('/dashboard/blocked-plan', { replace: true })
    }
  }

  if (response.status === HttpStatusCode.Forbidden) {
    handleForbiddenError(response, url)
  }

  if (response.status === HttpStatusCode.PaymentRequired) {
    const authStore = useAuthenticationStore()
    if (authStore.logged) {
      handlePaymentRequiredError()
    }
  }

  const handleMaintenanceError = (): void => {
    const lastSegment = url.split('?')[0].split('/').filter(Boolean).at(-1) ?? ''
    const isListingRequest = response.config?.method === HttpMethod.GET && !/^\d+$/.test(lastSegment)

    if (!isListingRequest) { return }

    const authStore = useAuthenticationStore()
    if (!authStore.logged) { return }

    const formModalStore = useFormModalStore()

    if (formModalStore.isOpen) {
      formModalStore.open({
        type: BaseModalType.Lateral,
        title: '',
        content: MaintenanceView,
        contentProps: {
          on: { retry: () => formModalStore.close() }
        },
        readonly: true,
        onDismiss: () => formModalStore.closeAll()
      })
      return
    }

    const retryPath = useNuxtApp().$router.currentRoute.value.fullPath
    navigateTo({ path: '/dashboard/maintenance', query: { from: retryPath } }, { replace: true })
  }

  if (MAINTENANCE_REDIRECT_STATUSES.has(response.status)) {
    handleMaintenanceError()
  }
}
